/* Scars to Strength — interactive rotating 3D book.
 * Pure Three.js (r128 global THREE). No OrbitControls, no CapsuleGeometry.
 * Rounded-box geometry built from ExtrudeGeometry so the spine + all cover
 * edges read as a real soft-edged perfect-bound book. 6 clean face materials.
 * Manual drag-to-rotate (rotation only, zoom + pan locked). Auto-rotate idle.
 * Lazy: caller injects three.js + this file only when the canvas nears view.
 */
(function () {
  // shared single-load guard: any src is fetched at most once, even if the
  // book and fountain both request three.min.js in the same frame.
  window.SB3DLoad = window.SB3DLoad || (function () {
    var cache = {};
    return function (src) {
      if (cache[src]) return cache[src];
      cache[src] = new Promise(function (res, rej) {
        var s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
      return cache[src];
    };
  })();

  function initBook(opts) {
    var mount = opts.mount;
    var TEX = opts.textures;
    var W = mount.clientWidth, H = mount.clientHeight || Math.round(W * 1.1);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(28, W / H, 0.1, 100);
    camera.position.set(0, 0, 8.2);

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2)); // cap DPR for mobile
    renderer.setSize(W, H);
    if (THREE.sRGBEncoding) renderer.outputEncoding = THREE.sRGBEncoding;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = 'pan-y'; // allow page scroll, we handle horizontal

    // ---- book dimensions (6x9 book, ~0.53in thick) ----
    var BH = 2.7;                       // height
    var BW = BH * (958 / 1442);         // width, from real cover ratio (0.664)
    var BT = BH / 15;                   // thickness
    var bevel = Math.min(BT * 0.32, 0.028);
    var maxAniso = renderer.capabilities.getMaxAnisotropy();

    // ---- textures ----
    var loader = new THREE.TextureLoader();
    function tex(url) {
      var t = loader.load(url);
      if (THREE.sRGBEncoding) t.encoding = THREE.sRGBEncoding;
      t.anisotropy = maxAniso;
      t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      return t;
    }
    var frontTex = tex(TEX.front);
    var backTex = tex(TEX.back);
    var spineTex = tex(TEX.spine);

    // subtle page-block: cream with faint horizontal lines
    var pageCanvas = document.createElement('canvas');
    pageCanvas.width = 8; pageCanvas.height = 256;
    var pc = pageCanvas.getContext('2d');
    pc.fillStyle = '#ede6d6'; pc.fillRect(0, 0, 8, 256);
    for (var y = 0; y < 256; y += 2) {
      pc.fillStyle = (y % 4 === 0) ? 'rgba(120,105,80,0.16)' : 'rgba(255,255,255,0.10)';
      pc.fillRect(0, y, 8, 1);
    }
    var pageTex = new THREE.CanvasTexture(pageCanvas);
    if (THREE.sRGBEncoding) pageTex.encoding = THREE.sRGBEncoding;
    pageTex.wrapS = pageTex.wrapT = THREE.RepeatWrapping;
    pageTex.anisotropy = maxAniso;

    var matFront = new THREE.MeshStandardMaterial({ map: frontTex, roughness: 0.62, metalness: 0.06 });
    var matBack  = new THREE.MeshStandardMaterial({ map: backTex,  roughness: 0.62, metalness: 0.06 });
    var matSpine = new THREE.MeshStandardMaterial({ map: spineTex, roughness: 0.6,  metalness: 0.08 });
    var matPage  = new THREE.MeshStandardMaterial({ map: pageTex,  color: 0xefe8d8, roughness: 0.9, metalness: 0.0 });
    var materials = [matFront, matBack, matSpine, matPage]; // indices: 0 front,1 back,2 spine,3 page

    // ---- rounded-book geometry via extruded rounded rectangle ----
    function roundedRectShape(w, h, r) {
      var s = new THREE.Shape();
      var x = -w / 2, y = -h / 2;
      s.moveTo(x + r, y);
      s.lineTo(x + w - r, y);
      s.quadraticCurveTo(x + w, y, x + w, y + r);
      s.lineTo(x + w, y + h - r);
      s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      s.lineTo(x + r, y + h);
      s.quadraticCurveTo(x, y + h, x, y + h - r);
      s.lineTo(x, y + r);
      s.quadraticCurveTo(x, y, x + r, y);
      return s;
    }
    var shape = roundedRectShape(BW, BH, 0.05);
    var geo = new THREE.ExtrudeGeometry(shape, {
      depth: BT - bevel * 2,
      bevelEnabled: true,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 3,
      steps: 1,
      curveSegments: 6
    });
    geo.center();
    geo.computeVertexNormals();
    geo = geo.toNonIndexed();

    // classify each triangle by face normal, recompute clean per-face UVs,
    // then sort triangles by material into contiguous groups.
    var pos = geo.attributes.position;
    var nor = geo.attributes.normal;
    var triCount = pos.count / 3;
    var bb = new THREE.Box3().setFromBufferAttribute(pos);
    var minX = bb.min.x, minY = bb.min.y, minZ = bb.min.z;
    var sizeX = bb.max.x - bb.min.x, sizeY = bb.max.y - bb.min.y, sizeZ = bb.max.z - bb.min.z;

    var P = [], N = [], U = [], M = [];
    var v = new THREE.Vector3(), n = new THREE.Vector3();
    for (var t = 0; t < triCount; t++) {
      var i0 = t * 3;
      // face normal = averaged vertex normal
      n.set(0, 0, 0);
      for (var k = 0; k < 3; k++) { n.x += nor.getX(i0 + k); n.y += nor.getY(i0 + k); n.z += nor.getZ(i0 + k); }
      n.normalize();
      var mat;
      if (n.z > 0.5) mat = 0;        // front (+Z)
      else if (n.z < -0.5) mat = 1;  // back (-Z)
      else if (n.x < -0.5) mat = 2;  // spine (-X)
      else mat = 3;                  // fore-edge / top / bottom (cream)
      for (var k2 = 0; k2 < 3; k2++) {
        v.set(pos.getX(i0 + k2), pos.getY(i0 + k2), pos.getZ(i0 + k2));
        P.push(v.x, v.y, v.z);
        N.push(nor.getX(i0 + k2), nor.getY(i0 + k2), nor.getZ(i0 + k2));
        var u1, u2;
        if (mat === 0) { u1 = (v.x - minX) / sizeX; u2 = (v.y - minY) / sizeY; }        // front
        else if (mat === 1) { u1 = 1 - (v.x - minX) / sizeX; u2 = (v.y - minY) / sizeY; } // back (mirror)
        else if (mat === 2) { u1 = (v.z - minZ) / sizeZ; u2 = (v.y - minY) / sizeY; }    // spine
        else { // cream page: run faint lines across thickness
          if (Math.abs(nor.getY(i0 + k2)) > 0.5) { u1 = (v.x - minX) / sizeX * 2; u2 = (v.z - minZ) / sizeZ * 30; }
          else { u1 = (v.y - minY) / sizeY * 2; u2 = (v.z - minZ) / sizeZ * 30; }
        }
        U.push(u1, u2);
        M.push(mat);
      }
    }
    // sort triangles by material into contiguous groups
    var order = [];
    for (var tt = 0; tt < triCount; tt++) order.push(tt);
    order.sort(function (a, b) { return M[a * 3] - M[b * 3]; });

    var nP = new Float32Array(triCount * 9), nN = new Float32Array(triCount * 9), nU = new Float32Array(triCount * 6);
    var groupRanges = {};
    for (var oi = 0; oi < order.length; oi++) {
      var src = order[oi];
      for (var c = 0; c < 3; c++) {
        var sIdx = src * 3 + c, dIdx = oi * 3 + c;
        nP[dIdx * 3] = P[sIdx * 3]; nP[dIdx * 3 + 1] = P[sIdx * 3 + 1]; nP[dIdx * 3 + 2] = P[sIdx * 3 + 2];
        nN[dIdx * 3] = N[sIdx * 3]; nN[dIdx * 3 + 1] = N[sIdx * 3 + 1]; nN[dIdx * 3 + 2] = N[sIdx * 3 + 2];
        nU[dIdx * 2] = U[sIdx * 2]; nU[dIdx * 2 + 1] = U[sIdx * 2 + 1];
      }
      var m = M[src * 3];
      if (!groupRanges[m]) groupRanges[m] = { start: oi * 3, count: 0 };
      groupRanges[m].count += 3;
    }
    var g2 = new THREE.BufferGeometry();
    g2.setAttribute('position', new THREE.BufferAttribute(nP, 3));
    g2.setAttribute('normal', new THREE.BufferAttribute(nN, 3));
    g2.setAttribute('uv', new THREE.BufferAttribute(nU, 2));
    [0, 1, 2, 3].forEach(function (m) {
      if (groupRanges[m]) g2.addGroup(groupRanges[m].start, groupRanges[m].count, m);
    });

    var book = new THREE.Mesh(g2, materials);
    scene.add(book);

    // ---- soft studio lighting ----
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 1.15); key.position.set(3, 4, 5); scene.add(key);
    var fill = new THREE.DirectionalLight(0xffe9d0, 0.5); fill.position.set(-4, 1, 3); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xfff4e6, 0.65); rim.position.set(-3, 2, -4); scene.add(rim);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x1a1a1a, 0.35));

    // ---- interaction: drag to rotate (rotation only) + idle auto-rotate ----
    book.rotation.x = (opts.startX != null) ? opts.startX : -0.12;
    book.rotation.y = (opts.startY != null) ? opts.startY : -0.55; // start: front + a hint of spine
    var FREEZE = !!opts.freeze;           // debug: hold a fixed angle
    var velY = 0, velX = 0, dragging = false, lastX = 0, lastY = 0, lastMoveT = 0;
    var AUTO = 0.0045;                    // slow auto-rotate (rad/frame)
    var idleFrames = 0;

    function px(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
    function py(e) { return e.touches ? e.touches[0].clientY : e.clientY; }
    function onDown(e) {
      dragging = true; idleFrames = 0; velY = velX = 0;
      lastX = px(e); lastY = py(e);
    }
    function onMove(e) {
      if (!dragging) return;
      var x = px(e), y = py(e);
      var dx = x - lastX, dy = y - lastY;
      lastX = x; lastY = y;
      velY = dx * 0.01; velX = dy * 0.008;
      book.rotation.y += velY;
      book.rotation.x = Math.max(-0.6, Math.min(0.6, book.rotation.x + velX));
      if (e.cancelable && Math.abs(dx) > Math.abs(dy)) e.preventDefault(); // block scroll only on horizontal drag
    }
    function onUp() { dragging = false; idleFrames = 0; }

    var el = renderer.domElement;
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('touchstart', onDown, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onUp);

    var running = true, raf = null;
    function animate() {
      if (!running) { raf = null; return; }
      raf = requestAnimationFrame(animate);
      if (!dragging && !FREEZE) {
        if (Math.abs(velY) > 0.0002 || Math.abs(velX) > 0.0002) { // inertia
          book.rotation.y += velY;
          book.rotation.x = Math.max(-0.6, Math.min(0.6, book.rotation.x + velX));
          velY *= 0.94; velX *= 0.94;
          idleFrames = 0;
        } else {
          idleFrames++;
          if (idleFrames > 45) {              // resume auto after brief pause
            book.rotation.y += AUTO;
            book.rotation.x += (-0.12 - book.rotation.x) * 0.02; // ease tilt back
          }
        }
      }
      renderer.render(scene, camera);
    }
    function play() { if (!raf) { running = true; animate(); } }
    function pause() { running = false; }
    // pause rendering while offscreen so the book + fountain never render together
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { en.isIntersecting ? play() : pause(); });
      }, { rootMargin: '0px' }).observe(mount);
    }
    animate();

    function resize() {
      var w = mount.clientWidth, h = mount.clientHeight || Math.round(w * 1.1);
      camera.aspect = w / h; camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', resize);

    return { destroy: function () { running = false; renderer.dispose(); if (el.parentNode) el.parentNode.removeChild(el); } };
  }

  // Lazy loader: inject three.js + init only when mount nears the viewport.
  function lazyBook(mount, textures, threeUrl) {
    var started = false;
    function boot() {
      if (started) return; started = true;
      function go() { initBook({ mount: mount, textures: textures }); }
      if (window.THREE) return go();
      window.SB3DLoad(threeUrl).then(go)['catch'](function (e) { if (window.console) console.warn('[book] three load failed', e); });
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { if (en.isIntersecting) { boot(); io.disconnect(); } });
      }, { rootMargin: '200px' });
      io.observe(mount);
    } else { boot(); }
  }

  window.ScarsBook = { init: initBook, lazy: lazyBook };
})();
